import { PROVIDER_CONFIGS } from '@config/providers/provider.config';
import { AIProviderBase, ProviderKey, ProviderCapabilities, MessageInterface, RequestConfig, FormattedRequest, ProviderResponse } from '@type/provider';

export class OpenAIProvider implements AIProviderBase {
  id: ProviderKey;
  name: string;
  endpoints: string[];
  models: string[];
  capabilities: ProviderCapabilities;

  constructor() {
    // Get configuration from provider config
    const config = PROVIDER_CONFIGS.openai;
    
    this.id = 'openai';
    this.name = config.name;
    this.endpoints = config.endpoints;
    this.models = config.models.map(m => m.id);
    this.capabilities = {
      supportsThinking: config.capabilities.supportsThinking,
      maxCompletionTokens: config.models[0]?.maxCompletionTokens ?? 4096,
      defaultModel: config.defaultModel
    };
  }

  formatRequest(messages: MessageInterface[], config: RequestConfig): FormattedRequest {
    // Filter out empty messages
    const filteredMessages = messages
      .filter(m => m.content.trim() !== '')
      .map(m => ({
        role: m.role,
        content: m.content
      }));
    
    // Create formatted request
    const formattedRequest: FormattedRequest = {
      model: config.model,
      max_tokens: config.max_tokens,
      max_completion_tokens: config.max_tokens,
      temperature: config.temperature,
      top_p: config.top_p,
      presence_penalty: config.presence_penalty,
      frequency_penalty: config.frequency_penalty,
      stream: config.stream ?? false,
      messages: filteredMessages
    };
    
    return formattedRequest;
  }

  parseResponse(response: unknown): string {
    const parsed = response as Partial<ProviderResponse> & { content?: string };

    // Validate response
    if (!parsed) return '';
    
    // Handle direct content (for test compatibility)
    if (parsed.content) {
      return parsed.content;
    }
    
    // Handle standard OpenAI response format
    if (parsed.choices?.[0]?.message?.content) {
      return parsed.choices[0].message.content;
    }
    
    throw new Error('Invalid response format from OpenAI');
  }

  parseStreamingResponse(response: unknown): string {
    try {
      const parsed = response as Partial<ProviderResponse>;

      // Validate response
      if (!parsed) return '';
      
      if (parsed.choices?.[0]?.delta) {
        return parsed.choices[0].delta.content || '';
      }
      return '';
    } catch (e) {
      console.error('Error parsing OpenAI response:', e);
      return '';
    }
  }
}
